import * as path from 'node:path';
import { getBridgeRoot } from '../../../files/cwd/index.js';
import { yieldToEventLoop } from '../../git-runtime.js';
import type { WorkingTreeChangeRepoDetail } from '../types.js';
import { buildWorkingTreeChangeRepoDetail } from './build-working-tree-change-repo-detail.js';
import { normalizeRepoPathRelativeToWorkspaceRoot } from './normalize-repo-path-relative-to-workspace-root.js';
import type { WorkingTreeRepoChangesBasis } from './types.js';

/**
 * Build per-repo working tree file lists for each git checkout the session would commit in.
 */
export async function getWorkingTreeChangeRepoDetails(options: {
  commitTargetPaths: string[];
  /** When set, isolated worktree scope: the session or preview parent path. */
  sessionParentPath: string | null;
  /** When set, only this repository (bridge-root-relative) is returned (one element). */
  repoFilterRelPath?: string | null;
  /** Defaults to uncommitted vs `HEAD`. Commit view requires `repoFilterRelPath`. */
  basis?: WorkingTreeRepoChangesBasis;
  /** Max recent commits to include per repo (sanitized server-side). */
  recentCommitsLimit?: unknown;
  /** When false (default), file rows omit patch content until fetched per file. */
  includeFilePatches?: boolean;
}): Promise<WorkingTreeChangeRepoDetail[]> {
  const bridgeRoot = path.resolve(getBridgeRoot());
  const sessionParentPath = options.sessionParentPath ? path.resolve(options.sessionParentPath) : null;
  const out: WorkingTreeChangeRepoDetail[] = [];
  const filter =
    options.repoFilterRelPath != null
      ? normalizeRepoPathRelativeToWorkspaceRoot(options.repoFilterRelPath)
      : null;
  const basisInput = options.basis ?? { kind: 'working' as const };
  if (basisInput.kind === 'commit' && !filter) {
    throw new Error('repoFilterRelPath is required for commit changes');
  }
  if (basisInput.kind === 'commit' && !basisInput.sha.trim()) {
    throw new Error('commit sha is required for commit changes');
  }

  const basis: WorkingTreeRepoChangesBasis =
    filter == null && basisInput.kind === 'commit' ? { kind: 'working' } : basisInput;

  for (let i = 0; i < options.commitTargetPaths.length; i++) {
    await yieldToEventLoop();
    const targetPath = options.commitTargetPaths[i]!;
    const detail = await buildWorkingTreeChangeRepoDetail({
      targetPath,
      bridgeRoot,
      sessionParentPath,
      repoFilterRelPath: filter,
      basis,
      recentCommitsLimit: options.recentCommitsLimit,
      includeFilePatches: options.includeFilePatches,
    });
    if (!detail) continue;

    out.push(detail);
    if (filter) return out;
  }

  return out;
}
